import { useEffect, useState, useCallback } from "react";
import { AiOutlineDelete } from "react-icons/ai";
import Layout from "./Layout";
import { ToastContainer, toast } from "react-toastify";
import Navbar from "../../Components/Navbar";
import Footer from "../../Components/Footer";
import axios from "../../axios/axios";
import PaginationControls from "../../Components/PaginationControls";
import RowsPerPageSelect from "../../Components/RowsPerPageSelect";
import ExportCsvButton from "../../Components/ExportCsvButton";
import DateRangeFilter from "../../Components/DateRangeFilter";

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchCoupons = useCallback(async () => {
    try {
      const response = await axios.get("/coupon/", {
        params: {
          page: currentPage,
          limit: rowsPerPage,
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
        },
      });
      setCoupons(response.data.results);
      setTotalPages(response.data.totalPages);
      setTotalResults(response.data.totalResults);
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred");
    }
  }, [currentPage, rowsPerPage, startDate, endDate]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Coupon?")) return;

    try {
      await axios.delete(`/coupon/${id}`);
      toast.success("Coupon deleted successfully!");
      fetchCoupons(); // refresh list
    } catch (error) {
      console.error("Failed to delete banner:", error);
      alert("Failed to delete banner");
    }
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setCurrentPage(1);
  };

  const fetchAllForExport = useCallback(async () => {
    const response = await axios.get("/coupon/", {
      params: {
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
      },
    });
    return response.data;
  }, [startDate, endDate]);

  const handleStartDateChange = (value) => {
    setStartDate(value);
    setCurrentPage(1);
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    setCurrentPage(1);
  };

  const handleClearDates = () => {
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  const csvColumns = [
    { label: "Coupon Code", accessor: (row) => row.code },
    { label: "Discount", accessor: (row) => `${row.discount}%` },
    { label: "Usage Limit", accessor: (row) => row.usageLimit },
    { label: "Used Count", accessor: (row) => row.usedCount },
  ];

  return (
    <>
      <Navbar />
      <Layout>
        <div className="bg-light py-5 px-3">
          <h3 className="text-700 mb-3">Manage Coupons</h3>
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
          />
          <div className="d-flex flex-wrap align-items-end justify-content-between gap-2 mb-2">
            <DateRangeFilter
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={handleStartDateChange}
              onEndDateChange={handleEndDateChange}
              onClear={handleClearDates}
            />
            <div className="d-flex align-items-center gap-3">
              <ExportCsvButton
                filename="coupons.csv"
                columns={csvColumns}
                fetchRows={fetchAllForExport}
              />
              <RowsPerPageSelect
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </div>
          </div>
          <table className="table table-hover table-light">
            <thead>
              <tr>
                <th>#</th>
                <th>Coupon Code</th>
                <th>Discount</th>
                <th>Usage Limit</th>
                <th>Used Count</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((coupon, index) => (
                <tr key={coupon._id}>
                  <td> {(currentPage - 1) * rowsPerPage + index + 1}</td>
                  <td> {coupon.code}</td>
                  <td> {coupon.discount}% off</td>
                  <td> {coupon.usageLimit}</td>
                  <td> {coupon.usedCount} </td>
                  <td className="">
                    <div className="d-flex gap-2">
                      <a
                        href="##"
                        className="delete"
                        title="Delete"
                        data-toggle="tooltip"
                      >
                        <i
                          onClick={() => handleDelete(coupon._id)}
                          className="material-icons"
                        >
                          <AiOutlineDelete />
                        </i>
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalResults={totalResults}
            onPageChange={(page) =>
              setCurrentPage(Math.min(Math.max(page, 1), totalPages || 1))
            }
          />
        </div>
      </Layout>
      <Footer />
    </>
  );
};

export default AdminCoupons;
